import { traversePropertyFn } from "@repo/dynamicEditor/src/helpers/dynamicProperties";
import { SectionMapping } from "@repo/dynamicEditor/src/helpers/sectionMapping";
import { component, section } from "@repo/dynamicEditor/src/models/component";
import { log } from "@repo/helpers";

import { componentType, MappedComponent } from "./components/portalDynamicComponent";
import { getSectionConditionalFor, matchConditional, shouldBeDisplayed, ValidationData } from "./validation";

export default class PortalSectionMapper<TProps extends ValidationData> extends SectionMapping<TProps> {
	protected mapSectionInternal(
		props: TProps,
		section: section,
		parentPropertyName?: string,
		iteratedTotal?: number,
		index?: number,
	) {
		const mappedSection = super.mapSectionInternal(props, section, parentPropertyName, iteratedTotal, index);
		mappedSection.visibleFor = getSectionConditionalFor(parentPropertyName, section as any).visibleFor;
		mappedSection.visible = shouldBeDisplayed(props)(mappedSection);
		return mappedSection;
	}

	protected mapComponentInternal(props: TProps, component: component, parentPropertyName?: string) {
		const mappedComponent = super.mapComponentInternal(props, component, parentPropertyName);
		mappedComponent.visible = shouldBeDisplayed(props)(mappedComponent);
		mappedComponent.required = mappedComponent.required || matchConditional(component.requiredFor, props);
		mappedComponent.disabled = matchConditional(component.disabledFor, props);

		const urlTypes = [];
		mappedComponent.hasUrl = mappedComponent.hasUrl || urlTypes.includes(mappedComponent.type);
		const loadHandledInternalTypes = [];
		mappedComponent.loadHandledInternal =
			mappedComponent.loadHandledInternal || loadHandledInternalTypes.includes(mappedComponent.type);
		const loadDataIsCustomTypes = [];
		mappedComponent.loadDataIsCustom =
			mappedComponent.loadDataIsCustom || loadDataIsCustomTypes.includes(mappedComponent.type);

		return mappedComponent;
	}

	protected getDerivedValueComponentInternal(
		props: TProps,
		parentPropertyName: string,
		component: component,
		propertyName?: string,
	) {
		switch (component.type as any) {
			case componentType.complaintLevel:
				return component.propertyName.split(",").map(traversePropertyFn(props, parentPropertyName));
		}
		return super.getDerivedValueComponentInternal(props, parentPropertyName, component, propertyName);
	}

	protected getHasUrl(mappedComponent: MappedComponent) {
		const urlTypes = [];
		return mappedComponent.hasUrl || urlTypes.includes(mappedComponent.type);
	}

	protected mapAddressComponents(
		addressComponent: MappedComponent,
		props: TProps,
		zipReadyOnly = false,
		cityReadOnly = true,
	) {
		super.mapAddressComponents(addressComponent, props, zipReadyOnly, cityReadOnly);
	}

	protected mapEntryTextWithLookupComponents(component: MappedComponent) {
		super.mapEntryTextWithLookupComponents(component);
	}
}

export const mapSection = <TProps extends ValidationData>(
	props: TProps,
	parentPropertyName?: string,
	iteratedTotal?: number,
) => {
	const mapper = new PortalSectionMapper();
	return mapper.mapSection(props, parentPropertyName, iteratedTotal);
};

export const mapComponent = <TProps extends ValidationData>(props: TProps, parentPropertyName?: string) => {
	const mapper = new PortalSectionMapper();
	return mapper.mapComponent(props, parentPropertyName);
};
